import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import _ from 'lodash';

const SwiftypeMeta = ({ name, dataType, content }) => (
  <Helmet>
    {_.isArray(content)
      ? content.map((contentItem, idx) => (
          <meta
            key={idx}
            class="swiftype"
            name={name}
            data-type={dataType}
            content={contentItem}
          />
        ))
      : content && (
          <meta
            class="swiftype"
            name={name}
            data-type={dataType}
            content={content}
          />
        )}
  </Helmet>
);

const SwiftypeTags = ({
  stack,
  title,
  excerpt,
  body,
  publishedAt,
  publishedAtMonth,
  contentType,
  product,
  productFamily,
  eventLocation,
  eventDate,
  application,
  researchArea,
  editingMethod,
  modelOrganism,
  customerAffiliation,
  paperType,
  featuredPublication,
  journal,
  pubmedId,
  author,
  image,
  tags,
  sections,
  journalLink,
  noIndex
}) => {
  // Wait until this component has rendered with data to prerender it
  // window.prerenderReady is set initially to false in index.html
  useEffect(() => {
    setTimeout(() => {
      if (_.get(stack, 'uid')) {
        window.prerenderReady = true;
      }
    }, 3000);
  }, [stack]);

  let defaults = {};

  if (stack) {
    // Flatten categories and tags
    const mappedCategoriesTags = _.mapValues(stack.categories_tags, function(
      catTags
    ) {
      if (_.isArray(catTags)) {
        return catTags.map(catTag => catTag.title);
      }
    });

    // Set defaults if no props, try to get data from known contentstack fields
    if (!image) {
      defaults.image =
        _.get(stack.meta, 'image.url') ||
        _.get(stack, 'hero_image.url') ||
        _.get(stack, 'main_image.url') ||
        _.get(stack, 'image.url');
    }
    if (!excerpt) {
      defaults.excerpt = stack?.meta?.description || stack.description;
    }
    if (!application) {
      defaults.application = _.get(mappedCategoriesTags, 'application');
    }
    if (!researchArea) {
      defaults.researchArea = _.get(mappedCategoriesTags, 'research_area');
    }
    if (!editingMethod) {
      defaults.editingMethod = _.get(mappedCategoriesTags, 'editing_method');
    }
    if (!modelOrganism) {
      defaults.modelOrganism = _.get(mappedCategoriesTags, 'model_organism');
    }
    if (!customerAffiliation) {
      defaults.customerAffiliation = _.get(
        mappedCategoriesTags,
        'customer_affiliation'
      );
    }
    if (!paperType) {
      defaults.paperType = _.get(mappedCategoriesTags, 'paper_type');
    }
    if (!product) {
      defaults.product = _.get(mappedCategoriesTags, 'product');
    }
    if (!productFamily) {
      defaults.productFamily = _.uniq(
        _.get(stack.categories_tags, 'product', []).map(product =>
          _.get(product, 'family[0].title')
        )
      );
    }
  }

  return (
    <>
      {noIndex && (
        <Helmet>
          <meta name="st:robots" content="noindex, nofollow" />
        </Helmet>
      )}
      <SwiftypeMeta
        name="excerpt"
        dataType="string"
        content={(excerpt || defaults.excerpt || '').replace(/<[^>]+>/g, '')}
      />
      <SwiftypeMeta name="title" dataType="string" content={title} />
      <SwiftypeMeta
        name="body"
        dataType="text"
        content={(body || '').replace(/<[^>]+>/g, '')}
      />
      <SwiftypeMeta name="published_at" dataType="date" content={publishedAt} />
      <SwiftypeMeta
        name="published_at_month"
        dataType="date"
        content={publishedAtMonth}
      />
      <SwiftypeMeta
        name="content_type"
        dataType="string"
        content={contentType}
      />
      <SwiftypeMeta
        name="product"
        dataType="string"
        content={product || defaults.product}
      />
      <SwiftypeMeta
        name="product_family"
        dataType="string"
        content={productFamily || defaults.productFamily}
      />
      <SwiftypeMeta
        name="event_location"
        dataType="location"
        content={eventLocation}
      />
      <SwiftypeMeta name="event_date" dataType="date" content={eventDate} />
      <SwiftypeMeta
        name="image"
        dataType="enum"
        content={image || defaults.image}
      />
      <SwiftypeMeta
        name="application"
        dataType="string"
        content={application || defaults.application}
      />
      <SwiftypeMeta
        name="research_area"
        dataType="string"
        content={researchArea || defaults.researchArea}
      />
      <SwiftypeMeta
        name="editing_method"
        dataType="string"
        content={editingMethod || defaults.editingMethod}
      />
      <SwiftypeMeta
        name="model_organism"
        dataType="string"
        content={modelOrganism || defaults.modelOrganism}
      />
      <SwiftypeMeta
        name="customer_affiliation"
        dataType="string"
        content={customerAffiliation || defaults.customerAffiliation}
      />
      <SwiftypeMeta
        name="paper_type"
        dataType="string"
        content={paperType || defaults.paperType}
      />
      <SwiftypeMeta
        name="journal_link"
        dataType="string"
        content={journalLink}
      />
      <SwiftypeMeta name="journal" dataType="string" content={journal} />
      <SwiftypeMeta name="pubmed_id" dataType="string" content={pubmedId} />
      <SwiftypeMeta name="tags" dataType="string" content={tags} />
      <SwiftypeMeta
        name="tags"
        dataType="string"
        content={_.get(stack, 'tags')}
      />
      <SwiftypeMeta name="author" dataType="string" content={author} />
      <SwiftypeMeta
        name="featured_publication"
        dataType="enum"
        content={featuredPublication}
      />
      <SwiftypeMeta name="sections" dataType="string" content={sections} />
    </>
  );
};

SwiftypeTags.defaultProps = {
  tags: [],
  sections: []
};

SwiftypeTags.propTypes = {
  title: PropTypes.string,
  body: PropTypes.string,
  excerpt: PropTypes.string,
  publishedAt: PropTypes.string,
  publishedAtMonth: PropTypes.string,
  contentType: PropTypes.string,
  product: PropTypes.string,
  productFamily: PropTypes.string,
  eventLocation: PropTypes.string,
  eventDate: PropTypes.string,
  tags: PropTypes.array,
  sections: PropTypes.array,
  noIndex: PropTypes.bool,
  application: PropTypes.array,
  researchArea: PropTypes.array,
  editingMethod: PropTypes.array,
  modelOrganism: PropTypes.array,
  paperType: PropTypes.array,
  featuredPublication: PropTypes.bool,
  journal: PropTypes.string,
  pubmedId: PropTypes.string,
  author: PropTypes.array
};

export default SwiftypeTags;
